import React from "react";
import { Checkbox, CheckboxProps } from "antd";
import { observer } from "mobx-react-lite";
import { CtrlSelection } from "src/components/tables/TableStore";
import { onError } from "src/common/onError";
import styles from "./TableSelection.module.less";

type PropsTableSelectionCheckbox<TRow extends {}> = {
  store: CtrlSelection<TRow>;
  row?: TRow;
  main?: boolean;
};

export const TableSelectionCheckbox = observer(
  <TRow extends {}>({
    store,
    main,
    row,
  }: PropsTableSelectionCheckbox<TRow>) => {
    const { onSelect } = store.selectionSettings;
    const { rowKey } = store;
    const selectedSet = new Set(store.selected.map((r) => r[rowKey]));
    const subSequenceSelectedNum = store.rows.reduce((acc, curr) => {
      if (selectedSet.has(curr[rowKey])) {
        return acc + 1;
      }
      return acc;
    }, 0);
    const isAllSelected = subSequenceSelectedNum === store.rows.length;
    const indeterminate =
      store.selected.length !== 0 &&
      store.selected.length !== store.rows.length &&
      !isAllSelected;

    const mainHandler = (checked: boolean) => {
      // Если checked = false, если верхний чекбокс кликнут в состоянии checked и должен перейти в unchecked (сброс всех)
      // Если checked = true, то исходное состояние unchecked или indeterminate и переход в checked (выбор всех)
      if (checked) {
        store.safeSelect(store.rows);
      } else {
        // Согласно LPLM-4230, сброс влияет не только на текущую страницу, но вообще на все выделенные строки
        store.setSelected([]);
      }
      onSelect?.(store.selected, store.rows, checked);
    };

    const certainHandler = (checked: boolean) => {
      try {
        if (!row) {
          throw new Error(`TableSelectionStd: row does not exist`);
        }
        const onPageKeys = new Set(store.rows.map((r) => r[rowKey]));
        const newSelected = store.selected.filter((r) =>
          onPageKeys.has(r[rowKey]),
        );
        store.safeSelect(
          checked
            ? [...newSelected, row]
            : newSelected.filter((r) => r[rowKey] !== row[rowKey]),
        );
        onSelect?.(store.selected, [row], checked);
      } catch (error) {
        onError(error);
      }
    };

    const handleOnChange = main ? mainHandler : certainHandler;
    const checkboxProps: CheckboxProps = main
      ? { checked: isAllSelected, indeterminate }
      : {
          checked: !!store.selected.find(
            (item) => item[rowKey] === row?.[rowKey],
          ),
        };

    return (
      <Checkbox
        {...checkboxProps}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onChange={(checked) => {
          handleOnChange(checked.target.checked);
        }}
        className={styles.selection}
      />
    );
  },
);
